import moment from 'moment';
import {
  AGUARDANDO_DIGITACAO,
  AGUARDANDO_INSPECAO,
  DIGITACAO_FINALIZADA,
  EM_ANALISE,
  EM_DIGITACAO,
  EM_INSPECAO,
  EXECUTADA_GDIS,
  INSPECAO_ENVIADA_COM_SUCESSO,
  INSPECAO_ENVIADA_PARCIAL,
  INSPECAO_EM_ENVIO,
  INSPECAO_FINALIZADA,
} from '../../../constants/status';
import { getPeriodoOptions } from '../Nsir/utils/usoMutuoPeriodo';

/** Chave virtual do KPI — Inspeção em andamento. */
export const KPI_EM_INSPECAO = 'KPI_EM_INSPECAO';

/** Chave virtual do KPI — Digitação em andamento. */
export const KPI_EM_DIGITACAO = 'KPI_EM_DIGITACAO';

/** Chave virtual do KPI — Finalizadas. */
export const KPI_FINALIZADAS = 'KPI_FINALIZADAS';

export const KPI_STATUS_LABELS = {
  [KPI_EM_INSPECAO]: 'Em Inspeção',
  [KPI_EM_DIGITACAO]: 'Em Digitação',
  [KPI_FINALIZADAS]: 'Finalizadas',
};

export const INSPECAO_ENVIADA_PARCIAL_COM_SUCESSO = 'INSPECAO_ENVIADA_PARCIAL_COM_SUCESSO';

export const KPI_CARD_GROUPS = [
  {
    key: KPI_EM_INSPECAO,
    label: KPI_STATUS_LABELS[KPI_EM_INSPECAO],
    color: 'warning',
    statuses: [
      AGUARDANDO_INSPECAO,
      EM_INSPECAO,
      INSPECAO_EM_ENVIO,
      INSPECAO_ENVIADA_PARCIAL,
      INSPECAO_ENVIADA_PARCIAL_COM_SUCESSO,
      EM_ANALISE,
    ],
  },
  {
    key: KPI_EM_DIGITACAO,
    label: KPI_STATUS_LABELS[KPI_EM_DIGITACAO],
    color: 'info',
    statuses: [AGUARDANDO_DIGITACAO, EM_DIGITACAO],
  },
  {
    key: KPI_FINALIZADAS,
    label: KPI_STATUS_LABELS[KPI_FINALIZADAS],
    color: 'success',
    statuses: [
      INSPECAO_FINALIZADA,
      INSPECAO_ENVIADA_COM_SUCESSO,
      DIGITACAO_FINALIZADA,
      EXECUTADA_GDIS,
    ],
  },
];

export const CHART_STATUS_ORDER = [
  KPI_EM_INSPECAO,
  KPI_EM_DIGITACAO,
  KPI_FINALIZADAS,
];

export const STATUS_COLOR = {
  TOTAL: 'info',
  [KPI_EM_INSPECAO]: 'warning',
  [KPI_EM_DIGITACAO]: 'info',
  [KPI_FINALIZADAS]: 'success',
};

export const VIEW_TAB_MATRIX = 'matrix';
export const VIEW_TAB_MALHA = 'malha';

/** Semanas exibidas no gráfico de evolução (alinhado a usoMutuoPeriodo WEEKS_BACK). */
export const HISTORICO_SEMANAS_DEFAULT = 10;

/** Opções do filtro local da Evolução (`historicoSemanas`). */
export const HISTORICO_SEMANAS_OPTIONS = [4, 8, 10, 12];

/** Quantidade de chips rápidos no header (esta + passada + recentes). */
export const HEADER_PERIODO_CHIP_COUNT = 5;

export const PERIODO_CHART_MODE_STACKED = 'stacked';
export const PERIODO_CHART_MODE_GROUPED = 'grouped';

export const EMPTY_RESUMO = {
  filtrado: {},
  total: {},
};

export const EMPTY_MATRIX = {
  periodoLabel: '',
  materialColumns: [],
  rows: [],
  totalsByMaterial: {},
  totalKg: 0,
  regionSubtotals: [],
};

export const EMPTY_METRIC = { nsirs: 0, totalKg: 0 };

export const formatPeriodoSucataLabel = (periodoInicio, periodoFim) => {
  if (periodoInicio == null || periodoFim == null) return '';
  const inicio = moment(periodoInicio);
  const fim = moment(periodoFim);
  if (!inicio.isValid() || !fim.isValid()) return '';
  if (inicio.year() === fim.year()) {
    return `${inicio.format('DD/MM')} – ${fim.format('DD/MM/YYYY')}`;
  }
  return `${inicio.format('DD/MM/YYYY')} – ${fim.format('DD/MM/YYYY')}`;
};

/** Label curto para chips de semana no header. */
export const formatPeriodoChipLabel = (periodoInicio, periodoFim) => {
  if (periodoInicio == null || periodoFim == null) return '';
  const inicio = moment(periodoInicio);
  const fim = moment(periodoFim);
  if (!inicio.isValid() || !fim.isValid()) return '';
  return `${inicio.format('DD/MM')}–${fim.format('DD/MM')}`;
};

export const getHeaderPeriodoChips = (options = getPeriodoOptions()) =>
  (options || []).slice(0, HEADER_PERIODO_CHIP_COUNT).map((option, index) => {
    let label = formatPeriodoChipLabel(option.periodoInicio, option.periodoFim);
    if (index === 0) label = 'Esta semana';
    if (index === 1) label = 'Semana passada';
    return {
      id: option.id,
      label,
      periodoInicio: option.periodoInicio,
      periodoFim: option.periodoFim,
    };
  });

export const getPeriodoHeaderPrefix = (periodoOptionId, options = getPeriodoOptions()) => {
  const currentId = options[0]?.id;
  if (currentId && periodoOptionId === currentId) return 'Semana corrente';
  return 'Semana selecionada';
};

/** Rótulo compacto para o eixo X do gráfico de evolução (11 semanas). */
export const formatPeriodoChartAxisTick = (periodoInicio, periodoFim, fallbackLabel) => {
  if (periodoInicio != null && periodoFim != null) {
    const inicio = moment(periodoInicio);
    const fim = moment(periodoFim);
    if (inicio.isValid() && fim.isValid()) {
      if (inicio.isSame(fim, 'day')) {
        return inicio.format('DD/MM/YY');
      }
      if (inicio.year() === fim.year() && inicio.month() === fim.month()) {
        return `${inicio.format('DD')}–${fim.format('DD/MM')}`;
      }
      if (inicio.year() === fim.year()) {
        return `${inicio.format('DD/MM')}\n${fim.format('DD/MM')}`;
      }
      return `${inicio.format('DD/MM/YY')}\n${fim.format('DD/MM/YY')}`;
    }
  }
  if (fallbackLabel) {
    return String(fallbackLabel).replace(/\s*–\s*/g, '\n');
  }
  return '—';
};

export const getDefaultPeriodo = () => {
  const options = getPeriodoOptions();
  const current = options[0];
  if (!current) {
    const inicio = moment().startOf('isoWeek').valueOf();
    const fim = moment().endOf('isoWeek').startOf('day').valueOf();
    return { periodoInicio: inicio, periodoFim: fim, periodoOptionId: '' };
  }
  return {
    periodoInicio: current.periodoInicio,
    periodoFim: current.periodoFim,
    periodoOptionId: current.id,
  };
};

export const getDefaultFilter = () => {
  const { periodoInicio, periodoFim, periodoOptionId } = getDefaultPeriodo();
  return {
    periodoInicio,
    periodoFim,
    periodoOptionId,
    newFlow: true,
  };
};

export const buildPeriodoFilterOptions = () =>
  getPeriodoOptions().map((option) => ({
    id: option.id,
    nome: option.nome,
  }));

const toArray = (v) => (Array.isArray(v) ? v : v != null && v !== '' ? [v] : []);

export const normalizeFilterForApi = (filter) => {
  if (!filter || typeof filter !== 'object') return {};
  const body = { newFlow: true };

  if (filter.periodoInicio != null) body.periodoInicio = filter.periodoInicio;
  if (filter.periodoFim != null) body.periodoFim = filter.periodoFim;
  if (filter.inicio != null) body.inicio = filter.inicio;
  if (filter.fim != null) body.fim = filter.fim;

  if (Array.isArray(filter.malhaIds) && filter.malhaIds.length > 0) {
    body.malhaIds = filter.malhaIds;
  } else if (filter.malhaId != null && filter.malhaId !== '') {
    body.malhaIds = toArray(filter.malhaId);
  }

  if (filter.gerenciaId) body.gerenciaId = filter.gerenciaId;

  if (Array.isArray(filter.inspetorIds) && filter.inspetorIds.length > 0) {
    body.inspetorIds = filter.inspetorIds;
  } else if (filter.inspetorId != null && filter.inspetorId !== '') {
    body.inspetorIds = toArray(filter.inspetorId);
  }

  if (filter.codigo) body.codigo = filter.codigo;

  if (Array.isArray(filter.status) && filter.status.length > 0) {
    body.status = filter.status;
  }

  if (filter.nsirId != null) body.nsirId = filter.nsirId;

  if (filter.malhaIdDrill != null) body.malhaId = filter.malhaIdDrill;

  if (filter.materialTipo) body.materialTipo = filter.materialTipo;

  return body;
};

export const normalizeResumo = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_RESUMO;
  }
  return {
    filtrado: data.filtrado && typeof data.filtrado === 'object' ? data.filtrado : {},
    total: data.total && typeof data.total === 'object' ? data.total : {},
  };
};

const normalizeKg = (raw) => {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object') {
    return Number(raw.totalKg) || 0;
  }
  return 0;
};

const normalizeNsirs = (raw) => {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object' && raw.nsirs != null) return Number(raw.nsirs) || 0;
  return 0;
};

export const getMetric = (counts, key) => ({
  nsirs: normalizeNsirs(counts?.[key]),
  totalKg: normalizeKg(counts?.[key]),
});

export const getGroupedMetric = (counts, statuses) => {
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return { ...EMPTY_METRIC };
  }
  return statuses.reduce(
    (acc, status) => ({
      nsirs: acc.nsirs + normalizeNsirs(counts?.[status]),
      totalKg: acc.totalKg + normalizeKg(counts?.[status]),
    }),
    { ...EMPTY_METRIC },
  );
};

export const sumMetrics = (counts) => {
  if (!counts || typeof counts !== 'object') return { ...EMPTY_METRIC };
  const fromTotal = counts.TOTAL;
  if (fromTotal != null) {
    return {
      nsirs: normalizeNsirs(fromTotal),
      totalKg: normalizeKg(fromTotal),
    };
  }
  return KPI_CARD_GROUPS.reduce(
    (acc, group) => ({
      nsirs: acc.nsirs + getMetric(counts, group.key).nsirs,
      totalKg: acc.totalKg + getMetric(counts, group.key).totalKg,
    }),
    { ...EMPTY_METRIC },
  );
};

export const resolveKpiStatusFilter = (kpiKey) => {
  if (!kpiKey) return null;
  const card = KPI_CARD_GROUPS.find((item) => item.key === kpiKey);
  return card ? card.statuses : null;
};

export const normalizeMatrix = (data) => {
  if (!data || typeof data !== 'object') {
    return { ...EMPTY_MATRIX };
  }
  return {
    periodoLabel: data.periodoLabel || '',
    materialColumns: Array.isArray(data.materialColumns) ? data.materialColumns : [],
    rows: Array.isArray(data.rows) ? data.rows : [],
    totalsByMaterial:
      data.totalsByMaterial && typeof data.totalsByMaterial === 'object'
        ? data.totalsByMaterial
        : {},
    totalKg: Number(data.totalKg) || 0,
    regionSubtotals: Array.isArray(data.regionSubtotals) ? data.regionSubtotals : [],
  };
};

export const formatKg = (value) => {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return '—';
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatKgMetric = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getChartColorForKpi = (status, theme) => {
  const { palette } = theme;
  const map = {
    [KPI_EM_INSPECAO]: palette.warning.main,
    [KPI_EM_DIGITACAO]: palette.info.main,
    [KPI_FINALIZADAS]: palette.success.main,
  };
  return map[status] || palette.primary.main;
};

export const getKpiLabel = (key) => KPI_STATUS_LABELS[key] || key;

export const applyPeriodoOptionToFilter = (filter, periodoOptionId, options) => {
  const match = (options || []).find((item) => item.id === periodoOptionId);
  if (!match) return filter;
  return {
    ...filter,
    periodoOptionId,
    periodoInicio: match.periodoInicio,
    periodoFim: match.periodoFim,
  };
};

/** Filtro para gráfico histórico — ignora período único e pede janela de semanas. */
export const buildHistoricoFilterForApi = (
  filter,
  historicoSemanas = HISTORICO_SEMANAS_DEFAULT,
) => {
  const body = normalizeFilterForApi(filter);
  delete body.periodoInicio;
  delete body.periodoFim;
  const n = Number(historicoSemanas);
  body.historicoSemanas = Number.isFinite(n) && n > 0 ? n : HISTORICO_SEMANAS_DEFAULT;
  return body;
};

export const EMPTY_PERIODO_HISTORICO = {
  items: [],
  materialColumns: [],
};

export const normalizePeriodoHistorico = (data) => {
  if (!data || typeof data !== 'object') {
    return { ...EMPTY_PERIODO_HISTORICO };
  }
  return {
    items: Array.isArray(data.items) ? data.items : [],
    materialColumns: Array.isArray(data.materialColumns) ? data.materialColumns : [],
  };
};

export const formatPeriodoChartLabel = (periodoInicio, periodoFim, fallbackLabel) => {
  if (fallbackLabel) return fallbackLabel;
  return formatPeriodoSucataLabel(periodoInicio, periodoFim);
};

export const isSamePeriodo = (aInicio, aFim, bInicio, bFim) => {
  if (aInicio == null || aFim == null || bInicio == null || bFim == null) return false;
  return moment(aInicio).isSame(moment(bInicio), 'day')
    && moment(aFim).isSame(moment(bFim), 'day');
};

export const isResumoEmpty = (resumo) => {
  const metric = sumMetrics(resumo?.filtrado);
  return metric.nsirs === 0 && metric.totalKg === 0;
};

/**
 * Item mais recente do histórico com kg > 0 → optionId.
 * Items da API vêm ordenados por periodoInicio ASC.
 */
export const resolveSuggestedPeriodoFromHistorico = (
  items,
  options = getPeriodoOptions(),
) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    const kg = Number(item?.totalKg) || 0;
    if (kg <= 0) continue;
    const match = (options || []).find((option) =>
      isSamePeriodo(
        option.periodoInicio,
        option.periodoFim,
        item.periodoInicio,
        item.periodoFim,
      ),
    );
    if (match) {
      return {
        periodoOptionId: match.id,
        periodoInicio: match.periodoInicio,
        periodoFim: match.periodoFim,
        label: formatPeriodoSucataLabel(match.periodoInicio, match.periodoFim),
      };
    }
  }
  return null;
};
